import { z } from "zod"
import { DurationSchema, TimelineSchema } from "./timing"

export const ReframeKeyframeSchema = z.object({
  id: z.string(),
  timeMs: z.number(),
  cropX: z.number(),
  cropY: z.number(),
  cropW: z.number(),
  cropH: z.number(),
  layout: z.string().default("single"),
  activeFaceTrack: z.string().nullable().default(null),
})

export const ReframeConfigSchema = z.object({
  enabled: z.boolean().default(true),
  layoutType: z.string().default("single"),
  autoDetectLayout: z.boolean().default(true),
  faceConfidenceThreshold: z.number().default(0.5),
  faceFilterMode: z.enum(["none", "standard", "aggressive"]).default("standard"),
  reduceFakeFace: z.boolean().default(false),
  minFaceSizeRatio: z.number().default(0.02),
  sceneDetectionThreshold: z.number().default(27.0),
  sceneDetectionEnabled: z.boolean().default(true),
  trackingIouThreshold: z.number().default(0.3),
  maxFramesToSkip: z.number().default(5),
  reframingStrategy: z.string().default("hybrid"),
  smoothTransitionFrames: z.number().default(15),
  speakerDetectionEnabled: z.boolean().default(true),
  speakerHighlightMode: z.string().default("focus"),
  interviewMode: z.boolean().default(false),
  preferDualForTwoSpeakers: z.boolean().default(true),
  speakerSwitchDelayMs: z.number().default(2000),
  speakerSwitchConfidence: z.number().default(0.7),
  activeSpeakerBorder: z.boolean().default(true),
  activeSpeakerBorderColor: z.string().default("#22c55e"),
  activeSpeakerBorderWidth: z.number().default(4),
  targetAspectRatio: z.string().default("9:16"),
  safeZonePadding: z.number().default(0.1),
})

export const FaceTrackSchema = z.object({
  trackId: z.string(),
  isSpeaker: z.boolean().default(false),
  speakerConfidence: z.number().default(0),
})

export const SceneSchema = z.object({
  startMs: z.number(),
  endMs: z.number(),
  strategy: z.string().default("track"),
})

export const ReframeContentSchema = z.object({
  id: z.string(),
  config: ReframeConfigSchema.default({}),
  keyframes: z.array(ReframeKeyframeSchema).default([]),
  faceTracks: z.array(FaceTrackSchema).default([]),
  scenes: z.array(SceneSchema).default([]),
})

export const ReframeSegmentSchema = z.object({
  id: z.string(),
  contentType: z.literal("Reframe"),
  duration: DurationSchema,
  timeline: TimelineSchema,
  content: ReframeContentSchema,
})

export type ReframeKeyframe = z.infer<typeof ReframeKeyframeSchema>
export type ReframeConfig = z.infer<typeof ReframeConfigSchema>
export type FaceTrack = z.infer<typeof FaceTrackSchema>
export type Scene = z.infer<typeof SceneSchema>
export type ReframeContent = z.infer<typeof ReframeContentSchema>
export type ReframeSegment = z.infer<typeof ReframeSegmentSchema>

/** @deprecated Use FaceTrack instead */
export type ReframeFaceTrack = FaceTrack
/** @deprecated Use Scene instead */
export type ReframeScene = Scene

export const DEFAULT_REFRAME_CONFIG: ReframeConfig = {
  enabled: false,
  layoutType: "single",
  autoDetectLayout: true,
  faceConfidenceThreshold: 0.5,
  faceFilterMode: "standard",
  reduceFakeFace: false,
  minFaceSizeRatio: 0.02,
  sceneDetectionThreshold: 27.0,
  sceneDetectionEnabled: true,
  trackingIouThreshold: 0.3,
  maxFramesToSkip: 5,
  reframingStrategy: "hybrid",
  smoothTransitionFrames: 15,
  speakerDetectionEnabled: true,
  speakerHighlightMode: "focus",
  interviewMode: false,
  preferDualForTwoSpeakers: true,
  speakerSwitchDelayMs: 2000,
  speakerSwitchConfidence: 0.7,
  activeSpeakerBorder: true,
  activeSpeakerBorderColor: "#22c55e",
  activeSpeakerBorderWidth: 4,
  targetAspectRatio: "9:16",
  safeZonePadding: 0.1,
}
